import React, { useState, useEffect } from "react";
import {
  Drawer,
  Form,
  Select,
  InputNumber,
  Button,
  Table,
  Typography,
  Tag,
  Card,
  Row,
  Col,
  Popconfirm,
  message,
} from "antd";
import {
  PlusOutlined,
  DeleteOutlined,
  DollarOutlined,
} from "@ant-design/icons";
import { supabase } from "../../../../lib/supabase";
import { PricingRule, PricingComponent, RuleComponent } from "../types";

const { Title, Text } = Typography;
const { Option } = Select;

interface ComponentsDrawerProps {
  visible: boolean;
  onClose: () => void;
  rule: PricingRule | null;
  components: PricingComponent[];
  onComponentsChange: () => void;
}

const ComponentsDrawer: React.FC<ComponentsDrawerProps> = ({
  visible,
  onClose,
  rule,
  components,
  onComponentsChange,
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [ruleComponents, setRuleComponents] = useState<RuleComponent[]>([]);
  const [addingComponent, setAddingComponent] = useState(false);

  // Load rule components when rule changes
  useEffect(() => {
    if (rule && visible) {
      loadRuleComponents();
    }
  }, [rule, visible]);

  const loadRuleComponents = async () => {
    if (!rule) return;

    try {
      setLoading(true);
      const { data, error } = await supabase
        .from("pricing_values")
        .select(
          `
          id, pricing_rule_id, pricing_component_id, value, currency,
          pricing_component:pricing_components(
            name, component_type, calculation_type
          )
        `
        )
        .eq("pricing_rule_id", rule.id);

      if (error) throw error;

      const mappedComponents: RuleComponent[] = (data || []).map(
        (comp: unknown) => {
          const component = comp as {
            id: number;
            pricing_rule_id: number;
            pricing_component_id: number;
            value: number;
            currency: string;
            pricing_component: {
              name: string;
              component_type: string;
              calculation_type: string;
            };
          };

          return {
            id: component.id,
            pricing_rule_id: component.pricing_rule_id,
            pricing_component_id: component.pricing_component_id,
            component_name: component.pricing_component.name,
            component_type: component.pricing_component.component_type,
            calculation_type: component.pricing_component.calculation_type,
            value: Number(component.value),
            currency: component.currency,
          };
        }
      );

      setRuleComponents(mappedComponents);
    } catch (error) {
      console.error("Error loading rule components:", error);
      message.error("Failed to load rule components");
    } finally {
      setLoading(false);
    }
  };

  const handleAddComponent = async (values: {
    component_id: number;
    value: number;
    currency: string;
  }) => {
    if (!rule) return;

    try {
      setAddingComponent(true);
      const { error } = await supabase.from("pricing_values").insert({
        pricing_rule_id: rule.id,
        pricing_component_id: values.component_id,
        value: values.value || 0,
        currency: values.currency || "EUR",
      });

      if (error) throw error;

      message.success("Component added successfully!");
      form.resetFields();
      await loadRuleComponents();
      onComponentsChange();
    } catch (error) {
      console.error("Error adding component:", error);
      message.error("Failed to add component");
    } finally {
      setAddingComponent(false);
    }
  };

  const handleUpdateComponent = async (
    componentId: number,
    field: string,
    value: unknown
  ) => {
    try {
      const { error } = await supabase
        .from("pricing_values")
        .update({ [field]: value })
        .eq("id", componentId);

      if (error) throw error;

      // Update local state
      setRuleComponents((prev) =>
        prev.map((comp) =>
          comp.id === componentId ? { ...comp, [field]: value } : comp
        )
      );

      onComponentsChange();
    } catch (error) {
      console.error("Error updating component:", error);
      message.error("Failed to update component");
    }
  };

  const handleRemoveComponent = async (componentId: number) => {
    try {
      const { error } = await supabase
        .from("pricing_values")
        .delete()
        .eq("id", componentId);

      if (error) throw error;

      message.success("Component removed successfully!");
      await loadRuleComponents();
      onComponentsChange();
    } catch (error) {
      console.error("Error removing component:", error);
      message.error("Failed to remove component");
    }
  };

  const getAvailableComponents = () => {
    const usedComponentIds = ruleComponents.map(
      (rc) => rc.pricing_component_id
    );
    return components.filter(
      (comp: PricingComponent) => !usedComponentIds.includes(comp.id)
    );
  };

  const getTotalCost = () => {
    return ruleComponents.reduce((sum, comp) => sum + comp.value, 0);
  };

  const columns = [
    {
      title: "Component",
      key: "component",
      render: (record: RuleComponent) => (
        <div>
          <Text strong>{record.component_name}</Text>
          <br />
          <Tag color="blue" style={{ fontSize: 11 }}>
            {record.component_type}
          </Tag>
          <Tag color="green" style={{ fontSize: 11 }}>
            {record.calculation_type}
          </Tag>
        </div>
      ),
    },
    {
      title: "Value",
      key: "value",
      width: 120,
      render: (record: RuleComponent) => (
        <InputNumber
          value={record.value}
          onChange={(value) =>
            handleUpdateComponent(record.id, "value", value || 0)
          }
          min={0}
          step={0.01}
          precision={2}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: "Currency",
      key: "currency",
      width: 100,
      render: (record: RuleComponent) => (
        <Select
          value={record.currency}
          onChange={(value) =>
            handleUpdateComponent(record.id, "currency", value)
          }
          style={{ width: "100%" }}
          size="small"
        >
          <Option value="EUR">EUR</Option>
          <Option value="USD">USD</Option>
          <Option value="GBP">GBP</Option>
          <Option value="CNY">CNY</Option>
        </Select>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      width: 80,
      align: "center" as const,
      render: (record: RuleComponent) => (
        <Popconfirm
          title="Remove this component?"
          onConfirm={() => handleRemoveComponent(record.id)}
          okText="Yes"
          cancelText="No"
        >
          <Button type="text" icon={<DeleteOutlined />} danger size="small" />
        </Popconfirm>
      ),
    },
  ];

  return (
    <Drawer
      title={
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <DollarOutlined />
          <span>Rule Components</span>
        </div>
      }
      open={visible}
      onClose={onClose}
      width={800}
      placement="right"
    >
      {rule ? (
        <div>
          {/* Rule Info */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Title level={5} style={{ margin: 0, marginBottom: 8 }}>
              {rule.name}
            </Title>
            <Row gutter={16}>
              <Col span={12}>
                <Text type="secondary">Transport: </Text>
                <Text>{rule.transport_type || "Any"}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Booking Type: </Text>
                <Text>{rule.shipment_type || "Any"}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Origin: </Text>
                <Text>{rule.origin_port || "Any"}</Text>
              </Col>
              <Col span={12}>
                <Text type="secondary">Destination: </Text>
                <Text>{rule.destination_port || "Any"}</Text>
              </Col>
            </Row>
          </Card>

          {/* Add Component Form */}
          <Card title="Add Component" size="small" style={{ marginBottom: 16 }}>
            <Form
              form={form}
              layout="inline"
              onFinish={handleAddComponent}
              style={{ width: "100%" }}
            >
              <Form.Item
                name="component_id"
                style={{ flex: 1 }}
                rules={[{ required: true, message: "Select a component" }]}
              >
                <Select placeholder="Select component">
                  {getAvailableComponents().map((comp: PricingComponent) => (
                    <Option key={comp.id} value={comp.id}>
                      {comp.name} ({comp.calculation_type})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
              <Form.Item
                name="value"
                style={{ width: 120 }}
                rules={[{ required: true, message: "Enter value" }]}
              >
                <InputNumber
                  placeholder="Value"
                  min={0}
                  step={0.01}
                  precision={2}
                />
              </Form.Item>
              <Form.Item name="currency" style={{ width: 80 }}>
                <Select defaultValue="EUR">
                  <Option value="EUR">EUR</Option>
                  <Option value="USD">USD</Option>
                  <Option value="GBP">GBP</Option>
                  <Option value="CNY">CNY</Option>
                </Select>
              </Form.Item>
              <Form.Item>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<PlusOutlined />}
                  loading={addingComponent}
                  disabled={getAvailableComponents().length === 0}
                >
                  Add
                </Button>
              </Form.Item>
            </Form>
          </Card>

          {/* Components Table */}
          <Card
            title={
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>Components ({ruleComponents.length})</span>
                <Text strong style={{ color: "#52c41a" }}>
                  Total: €{getTotalCost().toFixed(2)}
                </Text>
              </div>
            }
            size="small"
          >
            <Table
              columns={columns}
              dataSource={ruleComponents}
              loading={loading}
              rowKey="id"
              size="small"
              pagination={false}
              locale={{ emptyText: "No components added yet" }}
            />
          </Card>
        </div>
      ) : (
        <div style={{ textAlign: "center", padding: 40 }}>
          <Text type="secondary">Select a pricing rule to view components</Text>
        </div>
      )}
    </Drawer>
  );
};

export { ComponentsDrawer };
export default ComponentsDrawer;
